import React, { FC, useEffect, useRef, useState } from 'react'; 
import {
  Modal,
  Form,
  Button,
  Dropdown,
  DropdownButton,
  InputGroup,
  Container,
  Row,
  Col,
} from 'react-bootstrap';
import useAppToast from 'src/hooks/useAppToast';
import { userPhoneNumberAsync } from 'src/redux/actions/authActions';
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks';
import countries from 'src/utils/CountryCodes'; // Assuming this is your countries data
import Loader from 'src/utils/Loader';
import { OverlayTrigger, Tooltip } from 'react-bootstrap'; 
import { timeZoneToCountryMap } from 'src/utils/timeZoneToCountryMap';

// Assuming these types are used elsewhere, keeping them for context
type StepStatus = 'active' | 'inactive' | 'finished';
type TourSteps = {
  stepOne: StepStatus;
  stepTwo: StepStatus;
  stepThree: StepStatus;
};

type TourPopupPropsType = {
  setIsPhoneNumberStepDoneState: (value: boolean) => void;
};

const PhoneNumberPopUp: FC<TourPopupPropsType> = ({setIsPhoneNumberStepDoneState}) => {
  const [loading, setLoading] = useState(false);
  // Assuming stepsData and visible are part of your tourPopup state and used elsewhere
  const { stepsData, visible } = useAppSelector((state) => state.tourPopup);
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(false);
  const dispatch = useAppDispatch();
  const { successToast, warningToast } = useAppToast();

  const [selectedCountry, setSelectedCountry] = useState(
    countries.find((c) => c.code === 'IN') || countries[0] // Default to India, or the first country
  );
  const [countryLoading, setCountryLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [searchCountryQuery, setSearchCountryQuery] = useState(''); // State for the country search input
  const modalRef = useRef<HTMLDivElement>(null); // Ref for modal body to detect outside clicks

  function getCountryCodeFromBrowserTimeZone() {
    setCountryLoading(true);
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const countryCode = timeZoneToCountryMap[timeZone];

    if (countryCode) {
      const country = countries.find((c) => c.code === countryCode);
      if (country) {
        setSelectedCountry(country);
      }
      return countryCode;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error getting browser time zone:", error);
    return null;
  }finally{
    setCountryLoading(false);
  }
}

  useEffect(()=>{
    getCountryCodeFromBrowserTimeZone()
  },[])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !(modalRef.current as any).contains(event.target)) {
        // Optional: Handle clicks outside the modal if necessary (e.g., to close it)
        // However, this modal uses backdrop="static" so it won't close on outside click by default.
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCountryChange = (country: typeof countries[0]) => {
    setSelectedCountry(country);
    setSearchCountryQuery(''); // Clear the search query after a country is selected
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const cleanedInput = input.replace(/\D/g, ''); // Allow only digits
    setPhoneNumber(cleanedInput);
  };

  const validatePhoneNumber = (number: string): boolean => {
    const numericPhoneNumber = number.replace(/\D/g, '');
    return numericPhoneNumber.length >= 10 && numericPhoneNumber.length <= 13;
  };

  useEffect(() => {
    setIsPhoneNumberValid(validatePhoneNumber(phoneNumber));
  }, [phoneNumber]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // Prevent default form submission (page reload)

    if (!isPhoneNumberValid) {
      console.log('Phone number is not valid.');
      warningToast('Please enter a valid phone number (10-15 digits).');
      return;
    }

    setLoading(true); // Show loader during API call

    const payload = {
      phone_number: selectedCountry.dial_code + phoneNumber,
      country_code: selectedCountry.code,
      is_skipped: false,
    };

    console.log("payload", JSON.stringify(payload));
    try {
      const res: any = await dispatch(userPhoneNumberAsync(payload));
      console.log('response of platform choice', res.payload);

      if (res.payload?.status_code === 200) {
        successToast('Phone number added to your Account & can be edited later under Account Settings.');
        // Optionally, close modal or navigate after success
      } else {
        // Handle API-specific errors
        successToast("Phone number added to your Account & can be edited later under Account Settings.");
      }
    } catch (err: any) {
      console.error('Error submitting phone number:', err);
      warningToast('Facing issue: ' + (err.message || 'An unknown error occurred.'));
    } finally {
      setLoading(false); // Hide loader after API call completes
      setIsPhoneNumberStepDoneState(true);
    }
  };

  // Filtered countries based on the search query
  const filteredCountries = countries.filter((country) =>
    country.name.toLowerCase().includes(searchCountryQuery.toLowerCase()) || // Search by full name
    country.dial_code.includes(searchCountryQuery) || // Search by dial code (e.g., "+91")
    country.code.toLowerCase().includes(searchCountryQuery.toLowerCase()) // Search by country code (e.g., "in")
  );

  const handleClose = async() => {
    const payload = {
      phone_number: null,
      country_code: null,
      is_skipped: true,
    };

    try {
      const res: any = await dispatch(userPhoneNumberAsync(payload));

      if (res.payload?.status_code === 200) {
        successToast('Phone number added successfully!');
      } else {
        successToast(
          // res.payload?.message
          "Phone Number Skipped");
      }
    } catch (err: any) {
      warningToast('Facing issue: ' + (err.message || 'An unknown error occurred.'));
    } finally {
      setLoading(false);
      setIsPhoneNumberStepDoneState(true);
    }
  };


  return (
    <Modal
      show={true} 
      centered
      backdrop="static" // Prevents closing when clicking outside the modal
      size="lg"
      className="phone-number-modal" // Custom class for additional styling
      onHide={handleClose}
    >
      {/* Inline styles for demonstration. In a real project, move this to a CSS file. */}
      <style>
        {`
        .phone-number-modal .modal-dialog {
          max-width: 420px;
          margin: auto;
        }

        .phone-number-modal .modal-content {
          border-radius: 15px;
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
          border: none; /* HIGHLIGHT: Removed default border for cleaner look */
        }

        .phone-number-modal .modal-header {
          padding: 1.5rem 1.5rem 0.5rem 1.5rem; /* HIGHLIGHT: Adjusted padding for spacing */
          border-bottom: none;
          display: flex;
          justify-content: space-between; /* HIGHLIGHT: Used flexbox to space title and skip */
          align-items: center;
        }

        .phone-number-modal .modal-title {
          font-size: 1.35rem;
          color: #333;
          font-weight: 600;
          text-align: left; /* HIGHLIGHT: Aligned title to left */
          flex-grow: 1; /* HIGHLIGHT: Title takes available space */
        }

        .phone-number-modal .modal-header .btn-close {
            display: none; /* HIGHLIGHT: Hid the default close button */
        }

        .phone-number-modal .modal-body {
          padding: 0.5rem 1.5rem 1.5rem 1.5rem; /* HIGHLIGHT: Adjusted padding */
        }

        .phone-number-modal .form-label {
          font-weight: 500;
          color: #555;
          margin-bottom: 0.5rem;
        }

        .phone-number-modal .input-group .dropdown-toggle {
          border-top-left-radius: 8px; /* HIGHLIGHT: Slightly rounded corners */
          border-bottom-left-radius: 8px;
          border-right: none; /* HIGHLIGHT: Removed right border to merge with input */
          background-color: #fff; /* HIGHLIGHT: White background */
          display: flex;
          align-items: center;
          gap: 0.5rem; /* HIGHLIGHT: Space between flag and dial code */
          padding: 0.375rem 0.75rem; /* HIGHLIGHT: Adjusted padding */
          color: #333; /* HIGHLIGHT: Darker text color */
          box-shadow: none !important; /* HIGHLIGHT: Removed focus shadow */
        }

        .phone-number-modal .input-group .form-control {
          border-top-right-radius: 8px; 
          border-bottom-right-radius: 8px;
          border-top-left-radius: 0; 
          border-bottom-left-radius: 0;
          border-left: none; /* HIGHLIGHT: Removed left border */
          padding-left: 0.75rem; /* HIGHLIGHT: Padding inside the input */
          box-shadow: none !important; /* HIGHLIGHT: Removed focus shadow */
          /*border: 1px solid #ced4da;*/ /* HIGHLIGHT: Consistent border */
        }
        
        .phone-number-modal .input-group .form-control:focus {
            border-color: #80bdff;
            box-shadow: 0 0 0 0.25rem rgba(0, 123, 255, 0.25);
        }

        .phone-number-modal .dropdown-menu {
          max-height: 250px;
          overflow-y: auto;
          padding: 0;
          border-radius: 8px; 
          box-shadow: 0 5px 15px rgba(0,0,0,0.1); 
        }

        .phone-number-modal .dropdown-menu .form-control {
          margin: 0.5rem;
          width: calc(100% - 1rem);
        }

        .phone-number-modal .dropdown-item {
          padding: 0.75rem 1rem; 
          display: flex;
          align-items: center;
          gap: 0.75rem; 
        }

        .phone-number-modal .dropdown-item:hover,
        .phone-number-modal .dropdown-item:active {
          background-color: #f0f0f0; 
          color: #212529;
        }

        .phone-number-modal .btn-primary {
          background-color: #6a0dad; 
          border-color: #6a0dad;
          width: 100%; 
          border-radius: 8px; 
          font-weight: 500;
          padding: 0.75rem 1rem; 
          transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, transform 0.2s ease-in-out;
        }

        .phone-number-modal .btn-primary:hover {
          background-color: #5a0a9a; 
          border-color: #5a0a9a;
          transform: translateY(-2px); 
          box-shadow: 0 4px 10px rgba(106, 13, 173, 0.2); 
        }
        
        .phone-number-modal .btn-primary:disabled {
            background-color: #cccccc;
            border-color: #cccccc;
            transform: none;
            box-shadow: none;
            cursor: not-allowed;
        }

        .phone-number-modal .skip-text {
            color: #cccccc; /* HIGHLIGHT: Purple color for Skip */
            font-weight: 500;
            cursor: pointer;
            padding: 0.5rem; /* HIGHLIGHT: Added padding for click area */
        }
        .phone-number-modal .skip-text:hover {
            text-decoration: underline;
        }
        `}
      </style>
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <Loader variant="section" />
        </div>
      ) : (
        <>
          <Modal.Header>
            {/* HIGHLIGHT: Title structure changed for flex layout */}
            <Modal.Title>Add Your Phone Number</Modal.Title>
            {/* HIGHLIGHT: "Skip" text added in place of default close button */}
            <span className="skip-text" onClick={handleClose}>
              Skip
            </span>
          </Modal.Header>

          <Modal.Body ref={modalRef}>
            <Container>
              <Row className="justify-content-center">
                <Col xs={12}>
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-4">
                      <Form.Label className="text-muted">
                        Phone Number
                      </Form.Label>
                      {/* HIGHLIGHT: Removed shadow-sm, rounded, border-secondary classes here */}
                      <InputGroup style={{border: "1px solid #cccccc", borderRadius: "8px"}}>
                      {countryLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Loading...
                        </>
                        ) : (
                        <>
                        <Dropdown
                          onSelect={(eventKey) => {
                            const foundCountry = countries.find(
                              (country) => country.code === eventKey
                            );
                            if (foundCountry) {
                              handleCountryChange(foundCountry);
                            } else {
                              console.warn(`No country found for eventKey: ${eventKey}`);
                          }}}
                        >
                          <Dropdown.Toggle
                            variant="outline-secondary"
                            id="country-dropdown"
                            disabled={countryLoading} 
                            // HIGHLIGHT: Removed country-dropdown-button class, styling handled by direct CSS
                          >
                            <img
                              src={`https://flagcdn.com/w40/${selectedCountry.code.toLowerCase()}.png`}
                              alt={selectedCountry.code}
                              width="24"
                            />
                            {selectedCountry.dial_code}
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Form.Control
                              autoFocus
                              className="mx-3 my-2 w-auto"
                              placeholder="Search country..."
                              onChange={(e) =>
                                setSearchCountryQuery(e.target.value)
                              }
                              value={searchCountryQuery}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="dropdown-divider" />
                            {filteredCountries.length > 0 ? (
                              filteredCountries.map((country) => (
                                <Dropdown.Item
                                  key={country.code}
                                  eventKey={country.code} // HIGHLIGHT: Added eventKey for dropdown onSelect
                                  className="d-flex align-items-center"
                                >
                                  <img
                                    src={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png`}
                                    alt={country.code}
                                    width="20"
                                  />
                                  <span className="ms-2 flex-grow-1">
                                    {country.dial_code} {country.name}
                                  </span>
                                </Dropdown.Item>
                              ))
                            ) : (
                              <Dropdown.Item disabled>
                                No results found
                              </Dropdown.Item>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                        </>
                      )}
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            !isPhoneNumberValid && phoneNumber.length > 0 ? (
                              <Tooltip id="phone-tooltip">
                                Please enter a valid phone number.
                              </Tooltip>
                            ) : (
                              <></>
                            )
                          }
                        >
                          <Form.Control
                            type="tel"
                            placeholder="e.g., 78901 23456"
                            value={phoneNumber}
                            onChange={handlePhoneNumberChange}
                            maxLength={13}
                            aria-label="Phone number"
                            // HIGHLIGHT: Removed border border-start-1 ps-2 rounded classes here
                            isInvalid={
                              phoneNumber.length > 0 && !isPhoneNumberValid
                            }
                          />
                        </OverlayTrigger>
                      </InputGroup>
                    </Form.Group>

                    <div className="d-flex justify-content-end">
                      <Button
                        variant="primary"
                        type="submit"
                        // HIGHLIGHT: Removed w-25 and rounded-pill classes; now styled via CSS
                        className="fw-semibold"
                        disabled={!isPhoneNumberValid || loading}
                      >
                        {loading ? 'Adding...' : 'Continue'}
                      </Button>
                    </div>
                  </Form>
                </Col>
              </Row>
            </Container>
          </Modal.Body>
        </>
      )}
    </Modal>
  );
};

export default PhoneNumberPopUp;